import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { aiHelpService } from '../../lib/aiHelpService';
import { communicationService } from '../../lib/communicationService';
import { useAuth } from '../../contexts/AuthContext';

const AIHelpAssistant = ({ pageContext, pageName }) => {
  const { userProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentInteractionId, setCurrentInteractionId] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen && suggestedQuestions?.length === 0) {
      loadSuggestedQuestions();
    }
  }, [isOpen]);

  const loadSuggestedQuestions = async () => {
    try {
      const questions = await aiHelpService?.getSuggestedQuestions(
        pageContext,
        userProfile?.role || 'member'
      );
      setSuggestedQuestions(questions);
    } catch (error) {
      console.error('Error loading suggested questions:', error);
    }
  };

  const handleAskQuestion = async (questionText) => {
    if (!questionText?.trim()) return;

    setLoading(true);
    setResponse(null);
    setFeedbackSubmitted(false);
    setCurrentInteractionId(null);

    try {
      const helpResponse = await aiHelpService?.getContextualHelp(
        pageContext,
        questionText,
        {
          userRole: userProfile?.role,
          gymName: userProfile?.gym?.name
        }
      );

      setResponse(helpResponse);

      // Save interaction to database
      const { data } = await communicationService?.saveAIHelpInteraction(
        userProfile?.gym_id,
        pageContext,
        questionText,
        JSON.stringify(helpResponse)
      );

      if (data) {
        setCurrentInteractionId(data?.id);
      }
    } catch (error) {
      setResponse({
        answer: 'I apologize, but I\'m having trouble connecting to the help service right now. Please try again in a moment or contact support if the issue persists.',
        steps: [],
        relatedFeatures: [],
        tips: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (wasHelpful) => {
    if (!currentInteractionId) return;

    try {
      await communicationService?.submitAIHelpFeedback(
        currentInteractionId,
        wasHelpful,
        null
      );
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleSuggestedQuestionClick = (suggestedQuestion) => {
    setQuestion(suggestedQuestion);
    handleAskQuestion(suggestedQuestion);
  };

  const handleNewQuestion = () => {
    setQuestion('');
    setResponse(null);
    setFeedbackSubmitted(false);
    setCurrentInteractionId(null);
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        aria-label="AI Help Assistant"
      >
        {isOpen ? (
          <Icon name="X" size={24} />
        ) : (
          <Icon name="HelpCircle" size={24} />
        )}
      </button>
      {/* Help Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-h-[600px] bg-card rounded-xl shadow-2xl border border-border overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-primary text-primary-foreground p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
              <Icon name="Sparkles" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">AI Help Assistant</h3>
              <p className="text-xs opacity-90">{pageName || pageContext}</p>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!response ? (
              <>
                {/* Welcome Message */}
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-foreground">
                    Hi! I'm here to help you with <strong>{pageName || pageContext}</strong>. Ask me anything or choose a suggested question below.
                  </p>
                </div>

                {/* Suggested Questions */}
                {suggestedQuestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Suggested Questions
                    </p>
                    {suggestedQuestions?.map((sq, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestedQuestionClick(sq)}
                        className="w-full text-left p-3 bg-muted hover:bg-muted/80 rounded-lg text-sm text-foreground transition-colors"
                      >
                        {sq}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Question Asked */}
                <div className="bg-primary/10 rounded-lg p-3">
                  <p className="text-sm font-medium text-foreground">{question}</p>
                </div>

                {/* AI Response */}
                <div className="space-y-3">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-sm text-foreground">{response?.answer}</p>
                  </div>

                  {/* Steps */}
                  {response?.steps && response?.steps?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
                        Steps
                      </p>
                      <ol className="list-decimal list-inside space-y-1">
                        {response?.steps?.map((step, index) => (
                          <li key={index} className="text-sm text-foreground">
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Tips */}
                  {response?.tips && response?.tips?.length > 0 && (
                    <div className="bg-accent/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon name="Lightbulb" size={16} color="var(--color-accent-foreground)" />
                        <p className="text-xs font-semibold text-accent-foreground uppercase tracking-wide">
                          Tips
                        </p>
                      </div>
                      <ul className="space-y-1">
                        {response?.tips?.map((tip, index) => (
                          <li key={index} className="text-sm text-accent-foreground flex items-start gap-2">
                            <span className="text-accent-foreground mt-1">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Related Features */}
                  {response?.relatedFeatures && response?.relatedFeatures?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Related Features
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {response?.relatedFeatures?.map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-muted rounded-full text-xs text-foreground"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Feedback */}
                  {!feedbackSubmitted && currentInteractionId && (
                    <div className="border-t border-border pt-3 space-y-2">
                      <p className="text-xs text-muted-foreground">Was this helpful?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleFeedback(true)}
                          className="flex-1 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-700 dark:text-green-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <Icon name="ThumbsUp" size={16} />
                          Yes
                        </button>
                        <button
                          onClick={() => handleFeedback(false)}
                          className="flex-1 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <Icon name="ThumbsDown" size={16} />
                          No
                        </button>
                      </div>
                    </div>
                  )}

                  {feedbackSubmitted && (
                    <div className="text-center py-2">
                      <p className="text-xs text-green-600 dark:text-green-400">Thank you for your feedback!</p>
                    </div>
                  )}

                  {/* Ask Another Question */}
                  <button
                    onClick={handleNewQuestion}
                    className="w-full px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm font-medium transition-colors"
                  >
                    Ask Another Question
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Input Footer */}
          <div className="border-t border-border p-4">
            <form
              onSubmit={(e) => {
                e?.preventDefault();
                handleAskQuestion(question);
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e?.target?.value)}
                placeholder="Ask a question..."
                className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={loading}
              />
              <Button
                type="submit"
                disabled={loading || !question?.trim()}
                iconName={loading ? 'Loader2' : 'Send'}
                className="shrink-0"
              >
                {loading ? 'Thinking...' : 'Ask'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AIHelpAssistant;